import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Autocomplete,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import AlertModal from '../../components/AlertModal';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationOnIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useHistory, useLocation } from 'react-router-dom';
import { leadService } from '../../services/lead';
import { getProjects } from '../../services/project';
import { customerService } from '../../services/customer';
import { siteVisitService } from '../../services/siteVisit';
import { getAssociates } from '../../services/associate';
import { getBudgets, getSources, getLeadInterested } from '../../services/master';

// Simple debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const AddSiteVisit = () => {
  const history = useHistory();
  const location = useLocation();
  const visitData = location.state?.visitData || {};
  const leadData = location.state?.leadData || {};

  const [tabValue, setTabValue] = useState(0);
  const isEditMode = !!visitData?.id;
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [budgetList, setBudgetList] = useState([]);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [leadInterestedList, setLeadInterestedList] = useState([]);
  const [loadingInterested, setLoadingInterested] = useState(false);

  // Extract customer and lead data from visitData if editing
  const getInitialCustomerData = () => {
    if (isEditMode && visitData?.customerId) {
      // customerId can be an object with full customer details or just an ID
      if (typeof visitData.customerId === 'object' && visitData.customerId !== null) {
        return {
          id: visitData.customerId._id,
          _id: visitData.customerId._id,
          customerId: visitData.customerId._id,
          customerNo: visitData.customerId.customerNo || '',
          name: visitData.customerId.name || visitData.customer || '',
          phone: visitData.customerId.phone || visitData.mobileNumber || '',
          whatsapp: visitData.customerId.whatsapp || visitData.whatsappNumber || '',
          email: visitData.customerId.email || '',
          address: visitData.customerId.otherDetails?.address || visitData.address || '-',
        };
      }
    }
    return null;
  };

  const getInitialLeadData = () => {
    if (isEditMode && visitData?.leadId) {
      // leadId can be an object with full lead details or just an ID
      if (typeof visitData.leadId === 'object' && visitData.leadId !== null) {
        return {
          id: visitData.leadId._id,
          leadId: `LD${visitData.leadId.leadNo || visitData.leadId.leadId || ''}`,
          name: visitData.leadId.customerDetails?.name || visitData.leadId.name || '',
          phone: visitData.leadId.customerDetails?.phone || visitData.leadId.contact?.phone || '',
          whatsapp: visitData.leadId.customerDetails?.whatsapp || visitData.leadId.contact?.whatsapp || '',
          source: visitData.leadId.source || visitData.source || '',
          address: visitData.leadId.address?.addressLine || visitData.address || '-',
          assignedTo: visitData.leadId.associateDetails || visitData.leadId.assignedTo || null,
          assignedToName: visitData.leadId.associateDetails?.name || visitData.leadId.assignedTo?.name || '',
          assignedToPhone: visitData.leadId.associateDetails?.phone || visitData.leadId.assignedTo?.phone || '',
          assignedToId: visitData.leadId.associateDetails?._id || visitData.leadId.associateId || visitData.leadId.assignedTo?._id || visitData.leadId.assignedTo?.id || '',
        };
      }
    }
    return null;
  };

  const initialCustomer = getInitialCustomerData();
  const initialLead = getInitialLeadData();

  // Helper function to convert ISO date string to datetime-local format
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
      // If it's already in datetime-local format (YYYY-MM-DDTHH:mm), return as is
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Parse the date (handles ISO strings like "2025-11-20T13:22:00.000Z")
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateString);
        return '';
      }

      // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
      // Use local time, not UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      const formatted = `${year}-${month}-${day}T${hours}:${minutes}`;
      console.log('Date conversion:', dateString, '->', formatted);
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '';
    }
  };

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const showAlert = (message, type = 'info') => setAlertData({ open: true, message, type });

  const [formData, setFormData] = useState({
    // Details Section
    lead: visitData?.leadId ? (typeof visitData.leadId === 'object' ? `LD${visitData.leadId.leadNo || ''}` : visitData.leadId) : leadData?.leadId || '',
    source: visitData?.source || '',
    customer: initialCustomer?.name || visitData?.name || leadData?.name || '',
    customerId: initialCustomer?._id || visitData?.customerId?._id || (typeof visitData?.customerId === 'string' ? visitData.customerId : '') || visitData?.customer?._id || '',
    mobileNumber: initialCustomer?.phone || visitData?.mobileNumber || visitData?.phone || leadData?.phone || '',
    whatsappNumber: initialCustomer?.whatsapp || visitData?.whatsappNumber || visitData?.whatsapp || leadData?.whatsapp || '',
    address: initialCustomer?.address || visitData?.address || leadData?.address || '',
    interestedIn: visitData?.interestedIn || '',
    budgetUpto: visitData?.budgetUpto || '',
    // Projects Section
    projects: visitData?.projectsDetails?.map(p => p._id) || visitData?.projects || visitData?.projectIds || [],
    location: visitData?.location || '',
    sizeArea: visitData?.sizeArea || '',
    siteVisitBy: visitData?.siteVisitBy || visitData?.visitedBy || '',
    siteVisitByPhone: visitData?.siteVisitByPhone || visitData?.visitedByPhone || '',
    siteVisitByAssociateId: visitData?.siteVisitByAssociateId?._id || visitData?.siteVisitByAssociateId || visitData?.associateId || '',
    broker: visitData?.broker || '',
    visitDescription: visitData?.visitDescription || visitData?.visitRemark || '',
    remark1: visitData?.remark1 || '',
    remark2: visitData?.remark2 || '',
    // Follow-up Section
    inquiryStatus: visitData?.inquiryStatus || visitData?.status || '',
    nextFollowUpDate: formatDateForInput(visitData?.nextFollowUpDate || ''),
    followUpRemark: visitData?.followUpRemark || visitData?.lastFollowUpRemark || '',
    customerResponse: visitData?.customerResponse || '',
    customerFeedback: visitData?.customerFeedback || '',
    // Reference Section
    referenceName1: visitData?.referenceName1 || visitData?.referenceName || '',
    referencePhone1: visitData?.referencePhone1 || '',
    referenceName2: visitData?.referenceName2 || '',
    referencePhone2: visitData?.referencePhone2 || '',
    // Selfies
    selfies: visitData?.selfies || [],
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Legacy sourceOptions removed - using dynamic API data

  // Legacy interestedInOptions removed - using dynamic API data


  const inquiryStatusOptions = [
    'HOT',
    'WARM',
    'COLD',
    'Interested',
    'Not Interested',
    '50% intrested',
    'READY TO VISIT',
  ];

  const locationOptions = [
    'Select Locality',
    'Downtown',
    'Suburban',
    'Industrial Area',
    'Residential Zone',
    'Commercial District',
  ];

  // Fetch leads for dropdown - only if not editing or if leadId doesn't exist
  const fetchLeads = useCallback(async () => {
    if (leadsLoaded || loadingLeads) return;

    setLoadingLeads(true);
    try {
      const data = await leadService.getLeads({ page: 1, limit: 1000 });
      if (data && Array.isArray(data.docs)) {
        const mappedLeads = data.docs.map((l) => ({
          id: l._id,
          leadId: `LD${l.leadId ?? ''}`,
          name: l.customerDetails?.name || l.name || '-',
          phone: l.customerDetails?.phone || l.contact?.phone || '-',
          whatsapp: l.customerDetails?.whatsapp || l.contact?.whatsapp || '-',
          source: l.source ?? '-',
          address: l.address?.addressLine ?? '-',
          assignedTo: l.associateDetails || l.assignedTo || null,
          assignedToName: l.associateDetails?.name || l.assignedTo?.name || l.assignedToName || '',
          assignedToPhone: l.associateDetails?.phone || l.assignedTo?.phone || l.assignedToPhone || '',
          assignedToId: l.associateDetails?._id || l.associateId || l.assignedTo?._id || l.assignedTo?.id || '',
          customerId: l.customerId || l.customerDetails?.customerId || null, // Extract customerId from lead
        }));

        // Merge with existing leads (in case we already have initialLead)
        setLeads(prev => {
          const existingLeadIds = prev.map(l => l.id);
          const newLeads = mappedLeads.filter(l => !existingLeadIds.includes(l.id));
          return [...prev, ...newLeads];
        });

        // If leadData or visitData has lead info, set selected lead
        if (leadData?.id) {
          const foundLead = mappedLeads.find((l) => l.id === leadData.id);
          if (foundLead) {
            setSelectedLead(foundLead);
          }
        } else if (isEditMode && !initialLead) {
          const visitLeadId = visitData?.leadId || visitData?.lead;
          if (visitLeadId) {
            const foundLead = mappedLeads.find(l =>
              l.id === visitLeadId ||
              l.leadId === visitLeadId ||
              l.id === visitData?.leadIdObj ||
              l.leadId === visitData?.leadNo
            );
            if (foundLead) setSelectedLead(foundLead);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      // Fallback to mock data only if we don't have initialLead
      if (!initialLead) {
        setLeads([
          { id: '1', leadId: 'LD625', name: 'John Doe', phone: '9876543210', whatsapp: '9876543210', source: 'Website', address: '123 Main St, City' },
          { id: '2', leadId: 'LD626', name: 'Jane Smith', phone: '9876543211', whatsapp: '9876543211', source: 'Reference', address: '456 Park Ave, City' },
          { id: '3', leadId: 'LD627', name: 'Bob Johnson', phone: '9876543212', whatsapp: '9876543212', source: 'Walk-in', address: '789 Oak Rd, City' },
        ]);
      }
    } finally {
      setLoadingLeads(false);
      setLeadsLoaded(true);
    }
  }, [leadsLoaded, loadingLeads, initialLead, leadData]);

  // Fetch customers for dropdown - only if not editing or if customerId doesn't exist
  const fetchCustomers = useCallback(async () => {
    if (customersLoaded || loadingCustomers) return;

    setLoadingCustomers(true);
    try {
      const data = await customerService.getCustomers();
      if (Array.isArray(data)) {
        const mappedCustomers = data.map((c) => ({
          id: c._id,
          _id: c._id, // Store _id explicitly
          customerId: c._id,
          customerNo: c.customerNo || '',
          name: c.name || '-',
          phone: c.phone || c.mobile || '-',
          whatsapp: c.whatsapp || c.phone || c.mobile || '-',
          email: c.email || '-',
          address: c.address?.addressLine || c.address || '-',
        }));

        // Merge with existing customers (in case we already have initialCustomer)
        setCustomers(prev => {
          const existingCustomerIds = prev.map(c => c.id);
          const newCustomers = mappedCustomers.filter(c => !existingCustomerIds.includes(c.id));
          return [...prev, ...newCustomers];
        });

        // Try to find and set selected customer if not already available
        if (!initialCustomer) {
          const visitCustId = visitData?.customerId;
          const initialCustomerName = visitData?.name || leadData?.name || '';
          const foundCustomer = mappedCustomers.find(c =>
            (visitCustId && (c.id === visitCustId || c._id === visitCustId)) ||
            (initialCustomerName && c.name === initialCustomerName)
          );
          if (foundCustomer) {
            setSelectedCustomer(foundCustomer);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Don't set empty array if we have initialCustomer
      if (!initialCustomer) {
        setCustomers([]);
      }
    } finally {
      setLoadingCustomers(false);
      setCustomersLoaded(true);
    }
  }, [customersLoaded, loadingCustomers, initialCustomer, visitData, leadData]);

  // Fetch projects for dropdown - initial load (without filter)
  const fetchProjectsData = async (projectType = null) => {
    setLoadingProjects(true);
    try {
      const options = projectType ? { projectType } : {};
      const data = await getProjects(options);
      if (Array.isArray(data)) {
        const mappedProjects = data.map((p) => ({
          id: p._id,
          _id: p._id, // Store _id explicitly
          name: p.name || p.projectName || 'Unknown Project',
          projectId: p.projectId || p._id,
        }));
        setProjects(mappedProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Fallback to mock data
      setProjects([
        { id: '1', name: 'Project Alpha', projectId: 'PRJ001' },
        { id: '2', name: 'Project Beta', projectId: 'PRJ002' },
        { id: '3', name: 'Project Gamma', projectId: 'PRJ003' },
      ]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch associates for dropdown with search
  const fetchAssociates = useCallback(async (search = '') => {
    setLoadingAssociates(true);
    try {
      const params = { page: 1, limit: 100 };
      if (search) params.filterKey = search;
      
      const data = await getAssociates(params);
      // Handle different response structures
      let associatesList = [];
      if (data.success) {
        associatesList = Array.isArray(data.data) ? data.data : (data.data?.docs || []);
      } else if (Array.isArray(data)) {
        associatesList = data;
      } else if (data.data) {
        associatesList = Array.isArray(data.data) ? data.data : (data.data?.docs || []);
      }

      const mappedAssociates = associatesList.map((a) => ({
        id: a._id || a.id,
        _id: a._id || a.id, // Store _id explicitly
        name: a.name || a.associateName || '-',
        phone: a.phone || a.mobile || '-',
        email: a.email || '-',
      }));
      setAssociates(mappedAssociates);
    } catch (error) {
      console.error('Error fetching associates:', error);
      setAssociates([]);
    } finally {
      setLoadingAssociates(false);
    }
  }, []);

  const [errors, setErrors] = useState({});

  const validateMobile = (mobile) => {
    const regex = /^[6-9]\d{9}$/;
    return regex.test(mobile);
  };

  const validateField = (name, value) => {
    let errorMsg = '';
    switch (name) {
      case 'source':
        if (!value) errorMsg = 'Source is required';
        break;
      case 'customer':
        if (!value || !value.trim()) errorMsg = 'Customer Name is required';
        break;
      case 'siteVisitBy':
        if (!value || !value.trim()) errorMsg = 'Site Visit By is required';
        break;
      case 'inquiryStatus':
        if (!value) errorMsg = 'Inquiry Status is required';
        break;
      case 'nextFollowUpDate':
        if (!value) errorMsg = 'Next Follow Up Date is required';
        break;
      default:
        break;
    }
    return errorMsg;
  };

  const handleFieldBlur = (name) => {
    const errorMsg = validateField(name, formData[name]);
    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));
  };

  const debouncedFetchAssociates = useCallback(
    debounce((searchTerm) => {
      fetchAssociates(searchTerm);
    }, 500),
    [fetchAssociates]
  );

  // Fetch budgets for dropdown
  const fetchBudgets = async () => {
    setLoadingBudgets(true);
    try {
      const data = await getBudgets();
      setBudgetList(data || []);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setBudgetList([]);
    } finally {
      setLoadingBudgets(false);
    }
  };

  const fetchSourcesData = async () => {
    setLoadingSources(true);
    try {
      const data = await getSources();
      setSources(data || []);
    } catch (error) {
      console.error('Error fetching sources:', error);
      setSources([]);
    } finally {
      setLoadingSources(false);
    }
  };

  const fetchInterestedInData = async () => {
    setLoadingInterested(true);
    try {
      const data = await getLeadInterested();
      setLeadInterestedList(data || []);
    } catch (error) {
      console.error('Error fetching interested in options:', error);
      setLeadInterestedList([]);
    } finally {
      setLoadingInterested(false);
    }
  };

  // Initialize selected lead and customer from visitData if editing, and fetch other data
  useEffect(() => {
    // Update nextFollowUpDate if visitData has it and formData doesn't have it properly formatted
    if (isEditMode && visitData?.nextFollowUpDate && !formData.nextFollowUpDate) {
      const formattedDate = formatDateForInput(visitData.nextFollowUpDate);
      if (formattedDate) {
        setFormData((prev) => ({
          ...prev,
          nextFollowUpDate: formattedDate,
        }));
      }
    }

    if (isEditMode) {
      // Set selected lead if leadId exists in visitData
      if (initialLead) {
        setSelectedLead(initialLead);
        setLeads([initialLead]); // Add to leads list so it's available in dropdown
        setLeadsLoaded(true); // Mark as loaded so we don't fetch unnecessarily
      } else if (visitData?.leadId || visitData?.lead) {
        fetchLeads();
      }

      // Set selected customer if customerId exists in visitData
      if (initialCustomer) {
        setSelectedCustomer(initialCustomer);
        setCustomers([initialCustomer]); // Add to customers list so it's available in dropdown
        setCustomersLoaded(true); // Mark as loaded so we don't fetch unnecessarily
      } else if (visitData?.customerId || visitData?.customer) {
        fetchCustomers();
      }
    } else {
      // If leadData is provided (from LeadList), set it as selected and disabled
      if (leadData?._id || leadData?.leadId) {
        const leadFromData = {
          id: leadData._id || leadData.leadId || leadData.id,
          _id: leadData._id || leadData.leadId || leadData.id,
          leadId: `LD${leadData.leadNo || leadData.leadId || ''}`,
          name: leadData.customerDetails?.name || leadData.name || '',
          phone: leadData.customerDetails?.phone || leadData.phone || '',
          whatsapp: leadData.customerDetails?.whatsapp || leadData.whatsapp || '',
          source: leadData.source || '',
          address: leadData.address?.addressLine || leadData.address || leadData.location || '-',
        };
        setSelectedLead(leadFromData);
        setLeads([leadFromData]); // Add to leads list so it's available in dropdown
        setLeadsLoaded(true); // Mark as loaded so we don't fetch unnecessarily

        // Auto-fill form data from leadData
        setFormData((prev) => ({
          ...prev,
          lead: leadFromData.leadId,
          source: leadFromData.source || prev.source,
          customer: leadFromData.name || prev.customer,
          mobileNumber: leadFromData.phone || prev.mobileNumber,
          whatsappNumber: leadFromData.whatsapp || prev.whatsappNumber,
          address: leadFromData.address || prev.address,
        }));
      } else {
        // If not editing and no leadData, fetch leads and customers initially
        fetchLeads();
        fetchCustomers();
      }
    }

    // Always fetch projects and associates
    fetchProjectsData();
    fetchAssociates(); // Initial fetch for first 100
    fetchBudgets();
    fetchSourcesData();
    fetchInterestedInData();

    // Initialize selected associate if editing
    if (isEditMode && visitData?.siteVisitByAssociateId) {
      const initialAssoc = {
        id: typeof visitData.siteVisitByAssociateId === 'object' ? visitData.siteVisitByAssociateId._id : visitData.siteVisitByAssociateId,
        _id: typeof visitData.siteVisitByAssociateId === 'object' ? visitData.siteVisitByAssociateId._id : visitData.siteVisitByAssociateId,
        name: visitData.siteVisitBy || '',
        phone: visitData.siteVisitByPhone || '',
      };
      setSelectedAssociate(initialAssoc);
      setAssociates([initialAssoc]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch projects when interestedIn changes
  useEffect(() => {
    if (formData.interestedIn) {
      // Map interestedIn to projectType
      const projectTypeMap = {
        'Plot': 'Plot',
        'Flat': 'Apartment',
        'Villa': 'Villa',
        // 'Apartment': 'Apartment',
        'RowHouse': 'RowHouse',
        // 'Commercial': 'Commercial',
        // 'Residential': 'Residential',
      };

      const projectType = projectTypeMap[formData.interestedIn];
      if (projectType) {
        const fetchProjectsData = async () => {
          setLoadingProjects(true);
          try {
            const data = await getProjects({ projectType });
            if (Array.isArray(data)) {
              const mappedProjects = data.map((p) => ({
                id: p._id,
                name: p.name || p.projectName || 'Unknown Project',
                projectId: p.projectId || p._id,
              }));
              setProjects(mappedProjects);
            }
          } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
          } finally {
            setLoadingProjects(false);
          }
        };
        fetchProjectsData();
      }
    } else {
      // If no interestedIn selected, fetch all projects
      const fetchProjectsData = async () => {
        setLoadingProjects(true);
        try {
          const data = await getProjects();
          if (Array.isArray(data)) {
            const mappedProjects = data.map((p) => ({
              id: p._id,
              name: p.name || p.projectName || 'Unknown Project',
              projectId: p.projectId || p._id,
            }));
            setProjects(mappedProjects);
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
          setProjects([]);
        } finally {
          setLoadingProjects(false);
        }
      };
      fetchProjectsData();
    }
  }, [formData.interestedIn]);

  // Handle lead selection and auto-fill
  const handleLeadChange = (event, newValue) => {
    setSelectedLead(newValue);
    if (newValue) {
      // Format associate name with ID if available
      let associateName = '';
      if (newValue.assignedToName) {
        associateName = newValue.assignedToId
          ? `${newValue.assignedToName} ${newValue.assignedToId}`
          : newValue.assignedToName;
      }

      // Clear selected customer when lead is selected
      setSelectedCustomer(null);

      setFormData((prev) => ({
        ...prev,
        lead: newValue.leadId,
        source: newValue.source,
        customer: newValue.name,
        customerId: newValue.customerId || '', // Set customerId from lead
        mobileNumber: newValue.phone,
        whatsappNumber: newValue.whatsapp,
        address: newValue.address,
        // Auto-fill siteVisitBy with associate name if available
        siteVisitBy: associateName || prev.siteVisitBy,
        siteVisitByPhone: newValue.assignedToPhone || prev.siteVisitByPhone,
      }));
      setErrors((prev) => ({
        ...prev,
        customer: '',
        source: '',
        siteVisitBy: '',
      }));
    } else {
      // Clear fields when lead is deselected
      setFormData((prev) => ({
        ...prev,
        lead: '',
        source: '',
        customer: selectedCustomer ? prev.customer : '',
        customerId: selectedCustomer ? prev.customerId : '',
        mobileNumber: selectedCustomer ? prev.mobileNumber : '',
        whatsappNumber: selectedCustomer ? prev.whatsappNumber : '',
        address: selectedCustomer ? prev.address : '',
        siteVisitBy: '',
        siteVisitByPhone: '',
      }));
    }
  };

  // Handle customer selection and auto-fill
  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
    if (newValue) {
      // Clear selected lead when customer is selected
      setSelectedLead(null);

      setFormData((prev) => ({
        ...prev,
        lead: '', // Clear leadId when customer is selected
        customer: newValue.name,
        customerId: newValue._id || newValue.id || newValue.customerId || '',
        mobileNumber: newValue.phone,
        whatsappNumber: newValue.whatsapp,
        address: newValue.address,
      }));
      setErrors((prev) => ({
        ...prev,
        customer: '',
      }));
    } else {
      // Clear customer-related fields when customer is deselected
      setFormData((prev) => ({
        ...prev,
        customer: '',
        customerId: '',
        mobileNumber: '',
        whatsappNumber: '',
        address: '',
      }));
      setErrors((prev) => ({
        ...prev,
        customer: '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Numeric restriction for phone fields
    if (['mobileNumber', 'whatsappNumber', 'siteVisitByPhone', 'referencePhone1', 'referencePhone2'].includes(name)) {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length > 10) return; // Prevent more than 10 digits
      
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      return;
    }

    setFormData((prev) => {
      const newData = { ...prev, [name]: value };

      // Clear projects array only if user changed the interestedIn selection
      if (name === 'interestedIn' && prev.interestedIn !== value) {
        newData.projects = [];
      }

      return newData;
    });

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleProjectChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData((prev) => ({
      ...prev,
      siteVisitBy: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleProjectsChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData((prev) => ({
      ...prev,
      projects: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newSelfies = files.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      name: file.name,
      uploadDate: new Date().toLocaleDateString('en-GB'),
      address: formData.address || '',
      preview: URL.createObjectURL(file),
    }));
    setFormData((prev) => ({
      ...prev,
      selfies: [...prev.selfies, ...newSelfies],
    }));
  };

  const handleRemoveSelfie = (id) => {
    setFormData((prev) => ({
      ...prev,
      selfies: prev.selfies.filter((selfie) => selfie.id !== id),
    }));
  };

  const handleSubmit = async () => {
    const newErrors = {
      source: validateField('source', formData.source),
      customer: validateField('customer', formData.customer),
      siteVisitBy: validateField('siteVisitBy', formData.siteVisitBy),
      inquiryStatus: validateField('inquiryStatus', formData.inquiryStatus),
      nextFollowUpDate: validateField('nextFollowUpDate', formData.nextFollowUpDate),
    };

    setErrors(newErrors);

    const missingFields = [];
    const fieldLabels = {
      source: 'Source',
      customer: 'Customer Name',
      siteVisitBy: 'Site Visited By',
      inquiryStatus: 'Inquiry Status',
      nextFollowUpDate: 'Next Followup Date',
    };

    Object.keys(newErrors).forEach((field) => {
      if (newErrors[field]) {
        missingFields.push(fieldLabels[field]);
      }
    });

    if (missingFields.length > 0) {
      showAlert(`Please fill in all required fields:\n${missingFields.join(', ')}`, 'warning');

      const fieldOrder = [
        'source',
        'customer',
        'siteVisitBy',
        'inquiryStatus',
        'nextFollowUpDate',
      ];

      const firstErrorField = fieldOrder.find((field) => newErrors[field]);
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.getElementById(`field-${firstErrorField}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return;
    }

    // Phone number validations
    const phoneFields = [
      { name: 'referencePhone1', label: 'Reference Phone 1' },
      { name: 'referencePhone2', label: 'Reference Phone 2' },
      { name: 'whatsappNumber', label: 'WhatsApp Number' },
      { name: 'siteVisitByPhone', label: 'Site Visit By Phone' }
    ];

    for (const field of phoneFields) {
      const value = formData[field.name];
      if (value && value.length !== 10) {
        showAlert(`${field.label} must be exactly 10 digits`, 'Warning');
        return;
      }
    }

    setSaving(true);
    setSaveError('');

    try {
      // Get current user info for createdBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Extract leadId and leadNo from selectedLead
      // leadId should be the MongoDB _id, leadNo should be the formatted ID like "LD625"
      const leadId = selectedLead?.id || '';
      const leadNo = selectedLead?.leadId || formData.lead || '';

      // Extract associate ID from formData (from dropdown selection)
      const siteVisitByAssociateId = formData.siteVisitByAssociateId || '';

      // Format visitDate and visitTime - use current date/time for when visit is being recorded
      const visitDateTime = new Date();
      const visitDate = visitDateTime.toISOString();
      const visitTime = visitDateTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Format nextFollowUpDate
      const nextFollowUpDate = formData.nextFollowUpDate
        ? new Date(formData.nextFollowUpDate).toISOString()
        : '';

      // Map projects array - get _id from selected project IDs
      const projectIds = Array.isArray(formData.projects)
        ? formData.projects.map(projectId => {
          const project = projects.find(p => p.id === projectId);
          return project ? (project._id || project.id) : projectId;
        })
        : [];

      // Format selfies array
      const formattedSelfies = Array.isArray(formData.selfies)
        ? formData.selfies.map(selfie => ({
          id: selfie.id || `selfie_${Date.now()}_${Math.random()}`,
          file: selfie.file || selfie.url || '',
          name: selfie.name || 'Site Visit Photo',
          uploadDate: selfie.uploadDate ? new Date(selfie.uploadDate).toISOString() : new Date().toISOString(),
          address: selfie.address || formData.address || '',
          preview: selfie.preview || selfie.url || ''
        }))
        : [];

      // Prepare payload matching API structure
      const payload = {
        customer: formData.customer,
        mobileNumber: formData.mobileNumber || '',
        whatsappNumber: formData.whatsappNumber || '',
        address: formData.address || '',
        source: formData.source || '',
        interestedIn: formData.interestedIn || '',
        budgetUpto: formData.budgetUpto || '',
        visitDate: visitDate,
        visitTime: visitTime,
        projects: projectIds,
        location: formData.location || '',
        sizeArea: formData.sizeArea || '',
        siteVisitBy: formData.siteVisitBy || '',
        siteVisitByPhone: formData.siteVisitByPhone || '',
        broker: formData.broker || '',
        visitDescription: formData.visitDescription || '',
        remark1: formData.remark1 || '',
        remark2: formData.remark2 || '',
        inquiryStatus: formData.inquiryStatus,
        customerResponse: formData.customerResponse || '',
        customerFeedback: formData.customerFeedback || '',
        nextFollowUpDate: nextFollowUpDate,
        followUpRemark: formData.followUpRemark || '',
        referenceName1: formData.referenceName1 || '',
        referencePhone1: formData.referencePhone1 || '',
        referenceName2: formData.referenceName2 || '',
        referencePhone2: formData.referencePhone2 || '',
        selfies: formattedSelfies,
        createdBy: currentUserName
      };

      // Only include customerId if it has a value
      if (formData.customerId) {
        payload.customerId = formData.customerId;
      }

      // Only include leadId and leadNo if leadId has a value
      if (leadId) {
        payload.leadId = leadId;
        payload.leadNo = leadNo;
      }

      // Only include siteVisitByAssociateId if it has a value
      if (siteVisitByAssociateId) {
        payload.siteVisitByAssociateId = siteVisitByAssociateId;
      }

      // Determine redirect path based on where user came from
      const redirectPath = (leadData?._id || leadData?.leadId || leadData?.id)
        ? '/app/lead/LeadList'
        : '/app/siteVisit/SiteVisit';

      if (isEditMode && visitData?.id) {
        // Update existing site visit
        const response = await siteVisitService.updateSiteVisit(visitData.id, payload);
        if (response.success) {
          showAlert('Site visit updated successfully!', 'Success');
          setTimeout(() => history.push(redirectPath), 1500);
        } else {
          throw new Error(response.message || 'Failed to update site visit');
        }
      } else {
        // Create new site visit
        const response = await siteVisitService.createSiteVisit(payload);
        if (response.success) {
          showAlert('Site visit saved successfully!', 'Success');
          setTimeout(() => history.push(redirectPath), 1500);
        } else {
          throw new Error(response.message || 'Failed to create site visit');
        }
      }
    } catch (error) {
      console.error('Error saving site visit:', error);
      setSaveError(error.message || 'Failed to save site visit');
      showAlert(`Failed to save site visit: ${error.message}`, 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // If navigated from LeadList (has leadData), go back to LeadList
    if (leadData?._id || leadData?.leadId || leadData?.id) {
      history.push('/app/lead/LeadList');
    } else {
      history.push('/app/siteVisit/SiteVisit');
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={handleBack} sx={{ border: '1px solid #e0e0e0' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              {isEditMode ? 'Edit Site Visit' : 'Site Visit'}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={() => {
                const redirectPath = (leadData?._id || leadData?.leadId || leadData?.id)
                  ? '/app/lead/LeadList'
                  : '/app/siteVisit/SiteVisit';
                history.push(redirectPath);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              List
            </Button>
            <Button
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: 'primary.light' },
              }}
            >
              Add New
            </Button>
          </Box>
        </Box>
        {/* <Box sx={{ mt: 2, backgroundColor: 'primary.main', borderRadius: 1 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{
              '& .MuiTab-root': {
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48,
              },
              '& .Mui-selected': { color: '#fff' },
            }}
            // TabIndicatorProps={{ sx: {  height: 3 } }}
          >
            <Tab sx={{ color: '#fff', '&.Mui-selected': { color: '#fff' } }} label="Create" />
          </Tabs>
        </Box> */}
      </Box>

      {/* Form Content */}
      <Paper sx={{ p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        {/* Details Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={leads}
                getOptionLabel={(option) => `${option.name} - ${option.phone} - ${option.leadId}`}
                value={selectedLead}
                onChange={handleLeadChange}
                onOpen={() => {
                  // Only fetch leads when dropdown is opened and we don't have leadId in edit mode or leadData
                  if ((!isEditMode || !visitData?.leadId) && !leadData?._id && !leadData?.leadId) {
                    fetchLeads();
                  }
                }}
                loading={loadingLeads}
                disabled={!!leadData?._id || !!leadData?.leadId || !!selectedCustomer || isEditMode} // Disable if leadData, customer selected, or in edit mode
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Lead"
                    placeholder="Search by name, number, or ID"
                    disabled={!!leadData?._id || !!leadData?.leadId || !!selectedCustomer || isEditMode} 
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.leadId} | {option.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-source">
              <FormControl fullWidth size="small" required error={!!errors.source}>
                <InputLabel>Source *</InputLabel>
                <Select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('source')}
                  label="Source *"
                >
                  {sources.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                  {/* Fallback if no sources returned from API */}
                  {sources.length === 0 && (
                    ['Website', 'Reference', 'Walk-in', 'Social Media', 'Advertisement', 'Cold Call', 'Digital Marketing'].map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))
                  )}
                </Select>
                {errors.source && <FormHelperText>{errors.source}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-customer">
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => typeof option === 'string' ? option : `${option.name} - ${option.phone}`}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  onOpen={() => {
                    // Only fetch customers when dropdown is opened and we don't have customerId in edit mode
                    if (!isEditMode || !visitData?.customerId) {
                      fetchCustomers();
                    }
                  }}
                  loading={loadingCustomers}
                  disabled={!!selectedLead || isEditMode} // Disable if lead is selected or in edit mode
                  size="small"
                  sx={{ flex: 1 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer *"
                      placeholder="Search by name or phone"
                      required
                      onBlur={() => handleFieldBlur('customer')}
                      error={!!errors.customer}
                      helperText={errors.customer}
                      disabled={!!selectedLead || isEditMode} 
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.customerNo ? `${option.customerNo} | ` : ''}{option.phone}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  onInputChange={(event, newInputValue, reason) => {
                    // Allow free text input when user types manually
                    if (reason === 'input' && !selectedCustomer) {
                      setFormData((prev) => ({
                        ...prev,
                        customer: newInputValue,
                        // Clear customerId when user types manually (not selecting from dropdown)
                        customerId: '',
                      }));
                      setErrors((prev) => ({ ...prev, customer: '' }));
                    }
                  }}
                />
                <IconButton
                  sx={{
                    mt: 0.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                  size="small"
                  onClick={() => {
                    // Navigate to add customer page or open customer modal
                    // You can customize this action
                    console.log('Add new customer');
                  }}
                >
                  <PersonIcon sx={{ fontSize: 20 }} />
                  <AddIcon sx={{ fontSize: 14, ml: -1, mt: 0.5 }} />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                size="small"
                placeholder="Enter mobile number"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsappNumber"
                value={formData.whatsappNumber}
                onChange={handleChange}
                size="small"
                placeholder="Enter whatsapp number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select interested in</InputLabel>
                <Select
                  name="interestedIn"
                  value={formData.interestedIn}
                  onChange={handleChange}
                  label="Select interested in"
                  disabled={loadingInterested}
                >
                  {loadingInterested ? (
                    <MenuItem disabled>Loading options...</MenuItem>
                  ) : leadInterestedList.length > 0 ? (
                    leadInterestedList.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {option.leadInterested}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No options available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Budget To</InputLabel>
                <Select
                  name="budgetUpto"
                  value={formData.budgetUpto}
                  onChange={handleChange}
                  label="Select Budget To"
                  disabled={loadingBudgets}
                >
                  {loadingBudgets ? (
                    <MenuItem disabled>Loading budgets...</MenuItem>
                  ) : budgetList.length > 0 ? (
                    budgetList.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {option.budgetRange}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No budgets available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Projects Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Projects
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Project</InputLabel>
                <Select
                  multiple
                  name="projects"
                  value={Array.isArray(formData.projects) ? formData.projects : []}
                  onChange={handleProjectsChange}
                  input={<OutlinedInput label="Project" />}
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || selected.length === 0) {
                      return '';
                    }
                    return selected
                      .map((id) => {
                        const project = projects.find((p) => p.id === id);
                        return project ? project.name : id;
                      })
                      .join(', ');
                  }}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      <Checkbox checked={Array.isArray(formData.projects) && formData.projects.indexOf(project.id) > -1} />
                      <ListItemText primary={project.name} secondary={project.projectId} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Size/Area"
                name="sizeArea"
                value={formData.sizeArea}
                onChange={handleChange}
                size="small"
                placeholder="Size/Area"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-siteVisitBy">
              <Autocomplete
                options={associates}
                getOptionLabel={(option) => `${option.name} ${option.phone ? `- ${option.phone}` : ''}`}
                value={selectedAssociate}
                onChange={(event, newValue) => {
                  setSelectedAssociate(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    siteVisitByAssociateId: newValue ? newValue.id : '',
                    siteVisitBy: newValue ? newValue.name : '',
                    siteVisitByPhone: newValue ? newValue.phone : prev.siteVisitByPhone,
                  }));
                  setErrors((prev) => ({ ...prev, siteVisitBy: '' }));
                }}
                onInputChange={(event, newInputValue) => {
                  debouncedFetchAssociates(newInputValue);
                }}
                loading={loadingAssociates}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Site Visited By *"
                    placeholder="Search by name or number"
                    required
                    onBlur={() => handleFieldBlur('siteVisitBy')}
                    error={!!errors.siteVisitBy}
                    helperText={errors.siteVisitBy}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      {option.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {option.phone}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Phone"
                name="siteVisitByPhone"
                value={formData.siteVisitByPhone}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Broker"
                name="broker"
                value={formData.broker}
                onChange={handleChange}
                size="small"
                placeholder="Name"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Visit Description"
                name="visitDescription"
                value={formData.visitDescription}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
                placeholder="Enter Visit Description"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Remark 1"
                name="remark1"
                value={formData.remark1}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Enter Remark"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Remark 2"
                name="remark2"
                value={formData.remark2}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Enter Remark"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Follow-up Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Follow-up
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} id="field-inquiryStatus">
              <FormControl fullWidth size="small" required error={!!errors.inquiryStatus}>
                <InputLabel>Select *</InputLabel>
                <Select
                  name="inquiryStatus"
                  value={formData.inquiryStatus}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur('inquiryStatus')}
                  label="Select *"
                >
                  {inquiryStatusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {errors.inquiryStatus && <FormHelperText>{errors.inquiryStatus}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4} id="field-nextFollowUpDate">
              <TextField
                fullWidth
                label="Next Followup Dt. *"
                name="nextFollowUpDate"
                type="datetime-local"
                value={formData.nextFollowUpDate}
                onChange={handleChange}
                onBlur={() => handleFieldBlur('nextFollowUpDate')}
                error={!!errors.nextFollowUpDate}
                helperText={errors.nextFollowUpDate}
                size="small"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Followup Remark"
                name="followUpRemark"
                value={formData.followUpRemark}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Reference Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1976d2' }}>
            Reference
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="referenceName1"
                value={formData.referenceName1}
                onChange={handleChange}
                size="small"
                placeholder="Enter Reference Name"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="referencePhone1"
                value={formData.referencePhone1}
                onChange={handleChange}
                size="small"
                placeholder="Enter Ref. Phone No."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="referenceName2"
                value={formData.referenceName2}
                onChange={handleChange}
                size="small"
                placeholder="Enter Reference Name"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="referencePhone2"
                value={formData.referencePhone2}
                onChange={handleChange}
                size="small"
                placeholder="Enter Ref. Phone No."
              />
            </Grid>
          </Grid>
        </Box>

        {/* Selfies Section */}
        {/* <Box sx={{ mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Selfies
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CameraIcon />}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Upload Selfie
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {formData.selfies.length > 0 ? (
            <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>SrNo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Upload Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.selfies.map((selfie, index) => (
                    <TableRow key={selfie.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          {selfie.preview ? (
                            <img
                              src={selfie.preview}
                              alt={selfie.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#f5f5f5',
                              }}
                            >
                              <CameraIcon sx={{ fontSize: 24, color: '#999' }} />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{selfie.uploadDate}</TableCell>
                      <TableCell>{selfie.address || '-'}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSelfie(selfie.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                border: '1px dashed #e0e0e0',
                borderRadius: 2,
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No selfies uploaded yet. Click "Upload Selfie" to add images.
              </Typography>
            </Box>
          )}
        </Box> */}

        {/* Error Display */}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              backgroundColor: '#4caf50',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': { backgroundColor: '#45a049' },
              '&:disabled': { backgroundColor: '#cccccc' },
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>

      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : alertData.type.toLowerCase() === 'warning' ? 'warning' : 'info'}
        disableRestoreFocus={true}
      />
    </Box>
  );
};

export default AddSiteVisit;

